import type { CityConfig } from "../city-template/types";

export const lebanonConfig: CityConfig = {
  city: "Lebanon",
  state: "New Hampshire",
  stateAbbr: "NH",
  slug: "lebanon",
  route: "/cities/lebanon",
  title: "Emergency Passport Lebanon, NH | Fast Passport Renewal & Expedited Help",
  metaDescription: "Emergency passport and expedited passport services in Lebanon, NH — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Upper Valley and Grafton County travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Lebanon, NH",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Lebanon and Upper Valley travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Lebanon%20New%20Hampshire%20and%20Hanover%20New%20Hampshire%20view%20across%20the%20Connecticut%20River%2C%20charming%20Upper%20Valley%20New%20England%20town%20with%20historic%20Dartmouth%20College%20buildings%20visible%20across%20the%20river%2C%20green%20rolling%20hills%20and%20lush%20trees%2C%20warm%20golden%20hour%20light%2C%20vibrant%20and%20clean%20inviting%20scene%2C%20professional%20landscape%20photography%2C%20wide%20panoramic%20view%2C%20no%20visible%20text%20or%20people%2C%20soft%20earth%20tones%20and%20blue%20sky%2C%20photorealistic%20scenery&width=1800&height=700&seq=lebanon-nh-hero-001&orientation=landscape",
  heroImageAlt: "Lebanon New Hampshire Upper Valley view with the Connecticut River and historic Dartmouth College buildings across the river in Hanover",
  geoPosition: "43.6423;-72.2518",
  geoPlacename: "Lebanon, New Hampshire",
  regionalAgency: {
    name: "Boston Passport Agency",
    address: "5 Customs House St, Boston, MA 02103",
    distance: "Located in downtown Boston — approximately 2–2.5 hours from Lebanon via I-89 and I-93",
  },
  nearbyCitiesZips: [
    { name: "Lebanon", zips: "03766–03784" },
    { name: "Hanover", zips: "03755" },
    { name: "Claremont", zips: "03743" },
    { name: "Enfield", zips: "03748" },
    { name: "Canaan", zips: "03741" },
    { name: "Grantham", zips: "03753" },
    { name: "Plainfield", zips: "03781" },
    { name: "Cornish", zips: "03745" },
    { name: "Lyme", zips: "03768" },
    { name: "Orford", zips: "03777" },
    { name: "West Lebanon", zips: "03784" },
    { name: "Hartford", zips: "05047" },
    { name: "Norwich", zips: "05055" },
    { name: "White River Junction", zips: "05001" },
    { name: "Newport", zips: "03773" },
    { name: "Croydon", zips: "03773" },
    { name: "Sunapee", zips: "03782" },
    { name: "New London", zips: "03257" },
    { name: "Springfield", zips: "03284" },
    { name: "Sutton", zips: "03221" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Upper Valley departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Manchester", route: "/cities/manchester" },
    { name: "Nashua", route: "/cities/nashua" },
    { name: "Concord", route: "/cities/concord-nh" },
    { name: "Portsmouth", route: "/cities/portsmouth" },
    { name: "Keene", route: "/cities/keene" },
    { name: "Dover", route: "/cities/dover" },
    { name: "Rochester", route: "/cities/rochester" },
    { name: "Laconia", route: "/cities/laconia" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Lebanon remotely via secure courier. We do not have a walk-in office in Lebanon — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Upper Valley travelers.",
  fpcOfficeDistance: "Serving all of Grafton and Sullivan Counties",
  serviceAreaIntro: "From Lebanon's downtown Merchants Row to the West Lebanon commercial corridor, from the Hanover border across the Connecticut River to the Enfield line — Fast Passport Center serves the entire Lebanon community. We regularly help travelers from Hanover, Claremont, Enfield, and Canaan get their passports fast.",
  nearbyCitiesIntro: "We serve all Lebanon neighborhoods and nearby Upper Valley cities including Hanover, Claremont, Enfield, Canaan, Grantham, Plainfield, Cornish, Lyme, Orford, West Lebanon, and Hartford VT. If you are in the greater Lebanon area, we have you covered.",
  howItWorksIntro: "Most Lebanon travelers complete everything online and ship documents overnight. No need to drive to Boston or wait in line at the regional agency.",
  mistakesIntro: "With Lebanon Municipal Airport (LEB) and easy access to Manchester-Boston Regional (MHT), Upper Valley travelers can't afford passport delays. Here are the most common mistakes we see.",
  testimonialsIntro: "Lebanon and Upper Valley travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Lebanon? Here are the most common ones we hear from Upper Valley travelers.",
  statePageRoute: "/states/new-hampshire",
  statePageName: "New Hampshire",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Bereavement or medical crisis", "Expect to wait all day or return next day"],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Lebanon international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Dr. Sarah K.",
      location: "Lebanon, NH",
      rating: 5,
      text: "My conference in Geneva was in 3 days and my passport was expired. Fast Passport Center got me a new one in 36 hours. Absolutely saved my presentation.",
      service: "Emergency Passport",
    },
    {
      name: "Mark T.",
      location: "Hanover, NH",
      rating: 5,
      text: "Needed to renew for a family trip to Costa Rica. The process was smooth, they reviewed my documents, and I had my passport in 4 days. Highly recommend.",
      service: "Passport Renewal",
    },
    {
      name: "Rachel P.",
      location: "Claremont, NH",
      rating: 5,
      text: "Lost my passport the week before my honeymoon to Greece. They walked me through the DS-64 process and got me a replacement in 2 days. Incredible service.",
      service: "Lost Passport",
    },
    {
      name: "James L.",
      location: "Enfield, NH",
      rating: 5,
      text: "As a Dartmouth researcher with frequent international travel, I can't afford passport delays. Fast Passport Center has been my go-to for years. Professional and reliable.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "lebanon-1",
      name: "Lebanon Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "11 E Park St",
      city: "Lebanon",
      state: "NH",
      zip: "03766",
      phone: "(603) 448-4543",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.3 miles from downtown Lebanon",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary acceptance facility for Lebanon. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "lebanon-2",
      name: "Hanover Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "50 S Main St",
      city: "Hanover",
      state: "NH",
      zip: "03755",
      phone: "(603) 643-2171",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.4 miles from Dartmouth College",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Popular for Dartmouth College students and faculty. Appointment strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "lebanon-3",
      name: "Claremont Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "140 Broad St",
      city: "Claremont",
      state: "NH",
      zip: "03743",
      phone: "(603) 542-3701",
      hours: "Mon–Fri: 9:00 AM – 3:00 PM",
      distance: "0.5 miles from downtown Claremont",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Sullivan County residents. Limited hours — book appointments well in advance.",
      icon: "ri-mail-line",
    },
    {
      id: "lebanon-4",
      name: "West Lebanon Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "200 S Main St",
      city: "West Lebanon",
      state: "NH",
      zip: "03784",
      phone: "(603) 298-5523",
      hours: "Mon–Fri: 9:00 AM – 3:00 PM",
      distance: "0.6 miles from downtown West Lebanon",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Upper Valley commercial district residents. Photo services available. Book ahead.",
      icon: "ri-mail-line",
    },
    {
      id: "lebanon-5",
      name: "Newport Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "20 Main St",
      city: "Newport",
      state: "NH",
      zip: "03773",
      phone: "(603) 863-4311",
      hours: "Mon–Fri: 9:00 AM – 3:00 PM",
      distance: "0.4 miles from downtown Newport",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves northern Sullivan County. Limited passport hours — call to confirm.",
      icon: "ri-mail-line",
    },
    {
      id: "lebanon-6",
      name: "Lebanon City Clerk",
      type: "City Clerk Passport Acceptance",
      address: "51 N Park St",
      city: "Lebanon",
      state: "NH",
      zip: "03766",
      phone: "(603) 448-1524",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "0.1 miles from City Hall",
      website: "https://www.lebanonnh.gov/",
      websiteLabel: "Lebanon City Clerk",
      notes: "Major municipal facility with extended hours. Accepts walk-ins but appointments recommended for faster service.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — downtown Boston", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Lebanon photo shops often use generic sizes. Passport photos must be exactly 2x2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Grafton County acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Lebanon applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the Boston regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Lebanon families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Lebanon travelers often assume standard processing will be fine. It takes 6–13 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Lebanon." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Lebanon address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Lebanon before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Lebanon for travelers with urgent international flights. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, achieving 24–48 hour turnaround for most Upper Valley travelers." },
    { q: "How fast can I renew my passport in Lebanon?", a: "Fast Passport Center offers fast passport renewal in Lebanon in as little as 24–48 hours through our emergency courier service. For less urgent travel, our 3–5 day expedited option provides the best value." },
    { q: "What if my passport expires before my cruise?", a: "If your passport expires before your cruise, contact Fast Passport Center immediately. We can expedite your passport renewal in Lebanon in 24–48 hours so you don't miss your departure." },
    { q: "Can I get a same-day passport appointment near Lebanon?", a: "Same-day passport appointments are only available at the Boston Regional Passport Agency for documented life-or-death emergencies. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option for Lebanon residents." },
    { q: "Can I expedite a child passport in Lebanon?", a: "Yes. Fast Passport Center expedites child passports in Lebanon for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs." },
    { q: "What should I do if I lost my passport before a trip?", a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Lebanon with DS-64 form guidance. Our emergency courier service can get your replacement passport in 24–48 hours." },
    { q: "How fast can I get a passport in Lebanon?", a: "With Fast Passport Center's emergency courier service, Lebanon travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays." },
    { q: "Do I need to visit the Boston Passport Agency?", a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change." },
    { q: "Where are the closest passport acceptance facilities in Lebanon?", a: "Popular options include the Lebanon Post Office (03766), Hanover Post Office (03755), Claremont Post Office (03743), West Lebanon Post Office (03784), and Lebanon City Clerk (03766). All require appointments. We help you find the most convenient location and schedule your visit." },
    { q: "Can I get a same-day passport in Lebanon?", a: "Same-day passports are only available at the Boston Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency processing is the fastest reliable option." },
    { q: "What if I live in a nearby town like Hanover or Claremont?", a: "We serve all of the Upper Valley including Hanover, Claremont, Enfield, Canaan, Grantham, Plainfield, Cornish, Lyme, Orford, West Lebanon, and Newport. Our overnight shipping labels work from any UPS location, so your location doesn't affect processing speed." },
    { q: "How much does expedited passport service cost in Lebanon?", a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline." },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Lebanon", address: "7 E Park St, Lebanon, NH 03766", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Lebanon", address: "200 S Main St, Lebanon, NH 03784", hours: "Mon–Sun: 7AM–11PM" },
    { name: "FedEx Office — Lebanon", address: "20 Hanover St, Lebanon, NH 03766", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
    { name: "UPS Store — Lebanon", address: "10 Riverside Dr, Lebanon, NH 03766", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "Walmart — Lebanon", address: "460 N Plainfield Rd, Lebanon, NH 03766", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Rite Aid — Hanover", address: "11 Allen St, Hanover, NH 03755", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Postal Annex — Claremont", address: "140 Broad St, Claremont, NH 03743", hours: "Mon–Fri: 9AM–6PM, Sat: 9AM–4PM" },
    { name: "Costco — Lebanon", address: "1 Counterpoint Dr, Lebanon, NH 03766", hours: "Mon–Fri: 10AM–8:30PM, Sat–Sun: 9:30AM–6PM" },
  ],
  dmvLocations: [
    { name: "DMV — Lebanon", address: "480 N Plainfield Rd, Lebanon, NH 03766", hours: "Mon–Fri: 8AM–5PM", note: "REAL ID and driver's license renewals. Appointment required.", featured: true },
    { name: "DMV — Hanover", address: "50 S Main St, Hanover, NH 03755", hours: "Mon–Fri: 8AM–5PM", note: "Passport-ready photo services available. Online appointment booking.", featured: false },
    { name: "DMV — Claremont", address: "2 Industrial Dr, Claremont, NH 03743", hours: "Mon–Fri: 8AM–5PM", note: "REAL ID processing. Walk-ins limited — book online first.", featured: false },
    { name: "DMV — Newport", address: "10 Main St, Newport, NH 03773", hours: "Mon–Fri: 8AM–5PM", note: "Full-service center for northern New Hampshire residents.", featured: true },
  ],
  airports: [
    { code: "LEB", name: "Lebanon Municipal Airport", distance: "3 miles from downtown Lebanon", address: "5 Airpark Rd, West Lebanon, NH 03784", note: "Regional airport with commercial flights to Boston and New York. Convenient for Upper Valley travelers.", flightUrl: "https://www.lebanonairport.com/", flightLabel: "Lebanon Airport", featured: true },
    { code: "MHT", name: "Manchester-Boston Regional Airport", distance: "65 miles from Lebanon", address: "1 Airport Rd, Manchester, NH 03103", note: "Closest major commercial airport for Lebanon travelers. International flights to Canada and Europe. Major domestic hub.", flightUrl: "https://www.flymanchester.com/", flightLabel: "MHT Flight Info", featured: false },
    { code: "BOS", name: "Boston Logan International Airport", distance: "130 miles from Lebanon", address: "1 Harborside Dr, Boston, MA 02128", note: "Major international hub for long-haul flights to Europe, Asia, and South America. Accessible via I-89 and I-93.", flightUrl: "https://www.massport.com/logan-airport", flightLabel: "Logan Airport", featured: false },
  ],
};